'use client';

import { ChefHat, LogOut, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { APP_LOCALES, LOCALE_CONFIG } from '@/lib/constants';
import { useAppUser } from '@/lib/context/AppUserContext';
import { UserRole } from '@/lib/enums';

import {
  getNavigationCommandsForRoles,
  groupNavigationCommands,
  settingsCommands,
} from '../commands';
import { useCommandPaletteManager } from '../hooks/useCommandPaletteManager';

export function CommandPalette() {
  const { user } = useAppUser();
  const t = useTranslations('commandPalette');

  const userRoles = useMemo(() => {
    if (!user?.role) return [];
    return (Array.isArray(user.role) ? user.role : [user.role]) as UserRole[];
  }, [user?.role]);

  const {
    searchValue,
    setSearchValue,
    isOpen,
    employeeResults,
    dishResults,
    hasEmployeeResults,
    hasDishResults,
    hasResults,
    handleNavigate,
    handleLanguageChange,
    handleSettingsAction,
    handleOpenChange,
  } = useCommandPaletteManager(userRoles);

  const navigationCommands = useMemo(() => getNavigationCommandsForRoles(userRoles), [userRoles]);
  const groupedNavigation = useMemo(
    () => groupNavigationCommands(navigationCommands),
    [navigationCommands],
  );

  if (!user) return null;

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => handleOpenChange(open, navigationCommands.length)}
      title={t('title')}
    >
      <CommandInput
        placeholder={t('placeholder')}
        value={searchValue}
        onValueChange={setSearchValue}
      />
      <CommandList>
        <CommandEmpty>{t('noResults')}</CommandEmpty>

        {/* Dynamic Search Results - Dishes */}
        {hasDishResults && (
          <CommandGroup heading={t('groups.dishes')}>
            {dishResults.map((result) => (
              <CommandItem
                key={result.id}
                value={`dish-${result.id} ${result.label} ${result.description || ''}`}
                onSelect={() =>
                  handleNavigate(
                    {
                      href: result.href,
                      actionType: 'dish_search',
                      actionId: result.id,
                    },
                    navigationCommands.length,
                  )
                }
              >
                <ChefHat className="h-4 w-4" />
                <div className="flex flex-col">
                  <span>{result.label}</span>
                  {result.description && (
                    <span className="text-xs text-muted-foreground">{result.description}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Dynamic Search Results - Employees */}
        {hasEmployeeResults && (
          <CommandGroup heading={t('groups.employees')}>
            {employeeResults.map((result) => (
              <CommandItem
                key={result.id}
                value={`employee-${result.id} ${result.label} ${result.description || ''}`}
                onSelect={() =>
                  handleNavigate(
                    {
                      href: result.href,
                      actionType: 'employee_search',
                      actionId: result.id,
                    },
                    navigationCommands.length,
                  )
                }
              >
                <User className="h-4 w-4" />
                <div className="flex flex-col">
                  <span>{result.label}</span>
                  {result.description && (
                    <span className="text-xs text-muted-foreground">{result.description}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {hasResults && <CommandSeparator />}

        {/* Navigation Commands */}
        {Object.entries(groupedNavigation).map(([groupKey, commands]) => (
          <CommandGroup key={groupKey} heading={t(groupKey)}>
            {commands.map((command) => (
              <CommandItem
                key={command.id}
                value={[command.id, t(command.labelKey), ...(command.keywords || [])].join(' ')}
                onSelect={() =>
                  handleNavigate(
                    {
                      href: command.href,
                      actionType: 'navigation',
                      actionId: command.id,
                    },
                    navigationCommands.length,
                  )
                }
              >
                <command.icon className="h-4 w-4" />
                <span>{t(command.labelKey)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        <CommandSeparator />

        {/* Theme Commands */}
        <CommandGroup heading={t('groups.theme')}>
          {settingsCommands
            .filter((cmd) => cmd.action.startsWith('theme'))
            .map((command) => (
              <CommandItem
                key={command.id}
                value={`${t(command.labelKey)} ${command.keywords?.join(' ') || ''}`}
                onSelect={() => handleSettingsAction(command.action, command.id)}
              >
                <command.icon className="h-4 w-4" />
                <span>{t(command.labelKey)}</span>
              </CommandItem>
            ))}
        </CommandGroup>

        {/* Language Commands - only show locales supported by /app */}
        <CommandGroup heading={t('groups.language')}>
          {LOCALE_CONFIG.filter((locale) =>
            (APP_LOCALES as readonly string[]).includes(locale.code),
          ).map((locale) => (
            <CommandItem
              key={locale.code}
              value={`${locale.name} language idioma ${locale.code}`}
              onSelect={() => handleLanguageChange(locale.code)}
            >
              <span className="mr-2">{locale.flag}</span>
              <span>{locale.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Logout */}
        <CommandGroup heading={t('groups.account')}>
          <CommandItem
            value="logout sair exit sign out"
            onSelect={() => handleSettingsAction('logout', 'logout')}
          >
            <LogOut className="h-4 w-4" />
            <span>{t('settings.logout')}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
